import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { settingsAPI } from '@/lib/api';

const SettingsContext = createContext(null);

const FALLBACK = {
  company_name: 'PropNex',
  currency: 'USD',
  date_format: 'MM/DD/YYYY',
  auto_assignment: false,
  area_unit: 'sqft',
  pipeline_stages: [
    { key: 'neuer_lead', label: 'Neuer Lead', color: '#3B82F6' },
    { key: 'kontakt_hergestellt', label: 'Kontakt hergestellt', color: '#8B5CF6' },
    { key: 'probetraining_geplant', label: 'Probetraining geplant', color: '#F59E0B' },
    { key: 'probetraining_besucht', label: 'Probetraining besucht', color: '#06B6D4' },
    { key: 'angebot_gesendet', label: 'Angebot gesendet', color: '#10B981' },
    { key: 'abschluss', label: 'Abschluss', color: '#22C55E' },
    { key: 'nachfassen', label: 'Nachfassen', color: '#EC4899' },
    { key: 'reaktivierung', label: 'Reaktivierung', color: '#F97316' },
    { key: 'verloren', label: 'Verloren', color: '#EF4444' },
  ],
  terminal_stages: ['abschluss', 'verloren'],
  lead_sources: ['laufkundschaft', 'empfehlung', 'instagram', 'facebook', 'google', 'website', 'flyer', 'tag_der_offenen_tuer', 'schule_kita', 'verein', 'freunde', 'eltern', 'sonstiges'],
  property_types: ['sale', 'rent'],
  property_statuses: [
    { key: 'available', label: 'Available', color: '#10B981' },
    { key: 'reserved', label: 'Reserved', color: '#F59E0B' },
    { key: 'sold', label: 'Sold', color: '#3B82F6' },
    { key: 'rented', label: 'Rented', color: '#8B5CF6' },
  ],
  custom_fields_leads: [],
  custom_fields_properties: [],
  activity_types: [
    { key: 'call', label: 'Anruf' }, { key: 'email', label: 'E-Mail' },
    { key: 'meeting', label: 'Termin' }, { key: 'visit', label: 'Besuch' },
    { key: 'message', label: 'Nachricht' }, { key: 'note', label: 'Notiz' },
  ],
  follow_up_types: [
    { key: 'call', label: 'Anruf' }, { key: 'email', label: 'E-Mail' },
    { key: 'meeting', label: 'Termin' }, { key: 'visit', label: 'Besuch' },
  ],
  activity_outcomes: ['positive', 'neutral', 'negative', 'no_answer', 'callback_requested'],
  property_interests: ['residential', 'commercial', 'luxury', 'investment'],
  lead_timelines: ['immediate', '1-3 months', '3-6 months', '6-12 months'],
};

// Stale threshold — re-fetch if settings are older than 5 seconds
const STALE_MS = 5 * 1000;

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    // Skip if fetched recently (within STALE_MS) unless forced
    const now = Date.now();
    if (!force && lastFetchRef.current && (now - lastFetchRef.current) < STALE_MS) return;
    // Unterschrift-Station (03.10.2026): die Stationsseite und die QR-Seite des Kunden brauchen keine
    // CRM-Einstellungen — das Stationskonto darf sie nicht lesen (403), der Kunde ist nicht angemeldet (401)
    const pfad = window.location.pathname;
    if (pfad === '/unterschrift' || pfad.startsWith('/u/')) { setLoaded(true); return; }

    // Auth läuft primär über das httpOnly-Cookie. Nach einem Seiten-Reload ist
    // das In-Memory-Token leer, obwohl die Sitzung per Cookie noch gültig ist —
    // deshalb hier NICHT auf das In-Memory-Token prüfen, sondern den Abruf immer
    // versuchen. Das Cookie authentifiziert die Anfrage; bei fehlender Sitzung
    // kommt 401 und wird unten still ignoriert (Fallback-Werte bleiben stehen).
    try {
      const res = await settingsAPI.get();
      setSettings({ ...FALLBACK, ...res.data });
      lastFetchRef.current = Date.now();
    } catch (e) {
      // 401 (nicht angemeldet) still ignorieren — Fallback bleibt aktiv
    }
    setLoaded(true);
  }, []);

  // Initial fetch
  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  // Re-fetch on window focus (user switches back to tab)
  useEffect(() => {
    const onFocus = () => fetchSettings();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchSettings]);

  // Force refresh (called by Settings page after save)
  const refresh = useCallback(() => fetchSettings(true), [fetchSettings]);

  // Called on route change — re-fetches if stale
  const onNavigate = useCallback(() => fetchSettings(), [fetchSettings]);

  // Helpers
  const stageMap = {};
  (settings.pipeline_stages || []).forEach(s => { stageMap[s.key] = s; });

  const statusMap = {};
  const statuses = settings.property_statuses || [];
  if (Array.isArray(statuses)) {
    statuses.forEach(s => {
      if (typeof s === 'object' && s.key) statusMap[s.key] = s;
      else if (typeof s === 'string') statusMap[s] = { key: s, label: s.charAt(0).toUpperCase() + s.slice(1), color: '#6B7280' };
    });
  }

  const areaUnit = settings.area_unit || 'sqft';
  const areaLabel = areaUnit === 'sqm' ? 'sqm' : 'sqft';

  const formatArea = useCallback((value) => {
    if (value == null) return '—';
    return `${Number(value).toLocaleString()} ${areaUnit === 'sqm' ? 'sqm' : 'sqft'}`;
  }, [areaUnit]);

  const formatCurrency = useCallback((amount) => {
    if (amount == null) return '—';
    const sym = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', INR: '\u20B9', AUD: 'A$', CAD: 'C$', BRL: 'R$', JPY: '\u00A5', SGD: 'S$', AED: 'AED ', CHF: 'CHF ', CNY: '\u00A5' };
    const prefix = sym[settings.currency] || settings.currency + ' ';
    return `${prefix}${Number(amount).toLocaleString()}`;
  }, [settings.currency]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const fmt = settings.date_format || 'MM/DD/YYYY';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
    if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }, [settings.date_format]);

  const formatDateTime = useCallback((dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const datePart = formatDate(dateStr);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${time}`;
  }, [formatDate]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh, onNavigate, stageMap, statusMap, areaUnit, areaLabel, formatCurrency, formatDate, formatDateTime, formatArea }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be inside SettingsProvider');
  return ctx;
}
